/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// library imports
import { Component } from 'react';
import { Form, Button,  Modal, Spinner } from 'react-bootstrap';
import Axios from 'axios';
import ReactQuill from 'react-quill';
import moment from 'moment';
import { Redirect } from 'react-router-dom';
// project imports
import { getSellerUrl, getSellerHeaders } from 'shared/http';
import { loctemplatedetail, loctemplatetype, loctemplateupdate } from 'shared/settings/apiconfig';
import Validators from 'shared/validators';
// import toast from 'shared/toast';
import { ToasterMessage } from 'shared/toast/toast';

// style imports
import './LocationTemplateEdit.scss';

import { ExtendSnowTheme } from 'shared/editor/quill-editor';
import { NoData } from 'features/components/redirect/NoData';

/**
 * this page is used to edit location templates
 * 
 * @param templateId
 * 
 * @param props
 * 
 * @returns html code for rendering

**/

const quillAllowedRegexPattern =/^[A-Za-z0-9 .,!#\$%&\*\(\)\-\/'"\+\?<>\_\[\]:;@“”`‘’]+$/;

export class LocationTemplateEdit extends Component {

    templateData;
    modules;

    constructor(props) {
        super(props);
        this.textEditorRef = null;
        this.state = {
            // form
            errorpageredirect: false,
            formValid: false,
            formControls: {
                type: { value: '', valid: false, error: '', validators: [Validators.required()] },
                template: { value: '', valid: false, error: '', validators: [Validators.required()] }
            },

            // template preview modal toggle
            modal: false,

            // loading
            pageLoading: true,
            templateFormLoading: false,
            
            //template status
            templateStatus : { show: false, type: '', message: ''},
            errorType: ''
        };

        this.modules = {
            toolbar: [
                ['underline'], 
                [{ 'list': 'ordered' }, { 'list': 'bullet' },
                { 'indent': '-1' }, { 'indent': '+1' }],
                ['link'],
                ['clean']
            ],
            clipboard: {
                matchVisual: false,
                matchers: [
                    ["span", (node, delta) => {
                      delta.ops.forEach((op) => {
                        if (op.attributes) {
                          const isLink = !!op.attributes.link;
                          Object.keys(op.attributes).forEach((attr) => {
                            if (attr !== "link") delete op.attributes[attr]; 
                          });
                          if (!isLink) delete op.attributes.color;
                          delete op.attributes.background;
                        }
                      });
                      return delta;
                    }],
                
                    [Node.ELEMENT_NODE, (node, delta) => {
                      const blocked = [
                        "bold",
                        "italic",
                        "strike",
                        "size",
                        "font",
                        "header",
                        "color",
                        "background",
                      ];
                      delta.ops.forEach((op) => {
                        if (op.attributes) {
                          blocked.forEach((attr) => delete op.attributes[attr]);
                        }
                      });
                      return delta;
                    }],
                   
                    ["a", (node, delta) => delta],
                ],
            },
            magicUrl: true,
            keyboard: {
                bindings: {
                    // Disable Ctrl+B
                    bold: { key: "B", shortKey: true, handler: () => false },
                    // Disable Ctrl+I
                    italic: { key: "I", shortKey: true, handler: () => false },
                }
            }
        };
        
        this.templateData = {};
        this.approveBack = false;
    }

    componentDidMount() {
        this.getTemplateData();
        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', this.onBack);
    }

    onBack = (e) => {
        e.preventDefault();
        if (this.approveBack) {
            window.removeEventListener('popstate',this.onBack);
        } else {
            this.approveBack = true;
            this.navigateToList();
        }
    }

    getTemplateData = async () => {
        await Axios.get(getSellerUrl(`${loctemplatedetail}/${this.props.match.params['templateId']}`), getSellerHeaders())
            .then((response) => {
                if (response.status === 200) {
                this.templateData = { ...response.data['data'][0] };
                this.setState({
                    formControls: {
                        type: {
                            ...this.state.formControls.type,
                            value: response.data['data'][0]['type'],
                            valid: true
                        },
                        template: {
                            ...this.state.formControls.template,
                            value: JSON.parse(response.data['data'][0]['content'])['body'],
                            valid: true
                        }
                    }
                });
                //this.setState({ formValid: true });
            }})
            .catch((error) => {
                // toast.error(error.response.data['message']);
                if(error.response.status == 401 || error.response.status == 404) {
                    this.setState({
                        errorpageredirect: true,
                        errorType: error.response.status == 401 ? 'expiryError' : error.response.status == 404 ? 'noData' : ''
                    })
                }
            })
            .finally(() => {
                this.setState({ pageLoading: false });
            });
    };

    /* checks whether the input is valid or not */
    onInputValidation = (event) => {
        this.setState({ ...Validators.formValidation({ ...this.state.formControls }, event) })
    };

    onTemplateChange = (value) => {
        if (value === '<p><br></p>') {
            value = '';
        }

        this.onInputValidation({ target: { name: 'template', value: value } });
    };

    onEditorReset = async () => {
        this.setState({ pageLoading: false });
        await Axios.get(getSellerUrl(`${loctemplatetype}/${this.state.formControls.type.value}`), getSellerHeaders())
            .then((response) => {
                this.setState({
                    formControls: {
                        ...this.state.formControls,
                        template: {
                            ...this.state.formControls.template,
                            value: JSON.parse(response.data['data'][0]['content'])['body']
                        }
                    }
                });
                this.setState({ formValid: true });
            })
            .catch((error) => {
                this.setState({
                    templateStatus : { show: true, type: 'danger', message: error.response.data['message'] }
                }, () => {
                    setTimeout( () => {
                        this.setState({
                            templateStatus : { show: false, type: '', message: '' }
                        });
                    });
                });
            })
            .finally(() => {
                this.setState({ pageLoading: false });
            });
    };

    togglePreviewModal = (toggle) => {
        this.setState({ modal: toggle });
    };

    customDate = () => {
        if (this.templateData['modifieddate']) {
            return `${moment.utc(this.templateData['modifieddate']).local().format('MM/DD/YYYY hh:mm A')}`;
        }
        return '- - -';
    };

    onSaveTemplate = async (event) => {
        event.preventDefault();
        if (this.state.formValid) {
            this.setState({ templateFormLoading: true});
            const data = {
                id: this.props.match.params['templateId'],
                content: {
                    body: this.state.formControls.template.value,
                },
                type: this.state.formControls.type.value
            };

            await Axios.put(getSellerUrl(loctemplateupdate), JSON.stringify({ data: data }), getSellerHeaders())
                .then((response) => {
                    // toast.success('Successful');
                    this.setState({ 
                        templateFormLoading: false,
                        templateStatus: { show: true, type: 'success', message: 'Saved'}
                    }, () => {
                        setTimeout( () => {
                            this.setState({ templateStatus: { show: false, type: '', message: ''} })
                            // this.props.history.push({ pathname: '/locations/templates', state: this.props.location.state });
                        }, 3000);
                    }
                    );
                    
                })
                .catch((error) => {
                    // toast.error(error.response.data['message']);
                    this.setState({
                        templateStatus : { show: true, type: 'danger', message: error.response.data['message'] }
                    });
                });
        }
    };

    navigateToList = () => {
        this.props.history.push({ pathname: '/locations/templates', state: this.props.location.state });
    };

    textEditorOnShow = (event) => {
        this.textEditorRef.focus();
    }

    handleKeyDown = (event) => {
        const char = event.key;
        const controlKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Tab"
        ];
        if (controlKeys.includes(event.key)) {
        return; // allow navigation/edit keys
        }
        if (!quillAllowedRegexPattern.test(char)) {
          event.preventDefault();
        }
    };

    render() {
        if (this.state.errorpageredirect) {
            // redirect to Not-Found if wrong Locationid
            return <NoData {...this.props} type={this.state.errorType} />
          }
        return (
            <div className="d-flex flex-column location-template-edit">
                <div className="d-flex flex-row align-items-center border-bottom border-outline" style={{ minHeight: '40px' }}>
                    <div className="d-flex flex-row align-items-center justify-content-center border-right border-outline px-2 h-100">
                        <span onClick={this.navigateToList} className="clickable">
                            <i className="fa fa-chevron-circle-left fa-lg text-back rounded-circle"></i>
                            <span className="ml-2">Back</span>
                        </span>
                    </div>
                    <span className="text-secondary mx-2">{this.templateData['locationtitle']}</span>
                </div>
                {
                    this.state.pageLoading ?
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
                            <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="Please wait ....." height="100px" width="100px" />
                        </div> :
                        <div className="d-flex flex-row h-100 w-100">
                            <Form className="d-flex flex-column p-3 h-100 w-100" onSubmit={this.onSaveTemplate}>

                                <div className="float-control type">
                                    <div className="text">
                                        {
                                            this.state.formControls.type.error ? <small className="error">{this.state.formControls.type.error}</small> : null
                                        }
                                    </div>
                                    <Form.Control disabled className={`${this.state.formControls.type.value ? '' : 'text-muted'}`} id="type" name="type" value={ this.state.formControls.type.value === 'T & C' ? 'Terms & Conditions' : this.state.formControls.type.value } placeholder=" " onChange={(event) => this.onInputValidation(event)} />
                                    <label htmlFor="state">Type *</label>
                                </div>

                                <label>Template Editor *</label>
                                <div className={`text-editor border pb-4 mb-4 ${this.state.formControls.template.error ? 'border-danger' : 'border-outline'}`}
                                    style={{ position: 'relative' }}>
                                    <div className="wrapper">
                                    <ReactQuill theme="snow" style={{padding:"12px 15px"}} modules={this.modules} value={this.state.formControls.template.value} onChange={this.onTemplateChange} ref={(el) => this.textEditorRef = el} onKeyDown={this.handleKeyDown} />
                                    </div>
                                    <div style={{ position: 'absolute', bottom: '-22px', left: 0 }}>
                                        {
                                            this.state.formControls.template.error ? <small className="text-danger">{this.state.formControls.template.error}</small> : null
                                        }
                                    </div>
                                </div>

                                <div className="my-3">
                                    <div id="toaster" className= { this.state.templateStatus.show ? "d-flex flex-row align-items-center justify-content-center toaster ": ''}>
                                        {
                                        this.state.templateStatus.show ?
                                            <ToasterMessage type={this.state.templateStatus.type} message={this.state.templateStatus.message} className="alert-handle"></ToasterMessage>
                                            : null
                                        }
                                    </div>
                                    <div className="d-flex flex-row align-items-center justify-content-center my-3" style={{flexFlow: "wrap"}}> 
                                        <div>
                                        <Button type="button" variant="outline-dark mx-1" style={{ width: '90px' }}
                                            onClick={this.navigateToList}>Cancel</Button>
                                        <Button type="button" variant="outline-dark btn-white mx-1 my-2" style={{ width: '90px' }} onClick={this.onEditorReset}>Reset</Button>
                                        </div>
                                        <div>
                                        <Button type="button" variant="mx-1" style={{ width: '90px', backgroundColor:'#00263E', color:'white' }} onClick={() => this.togglePreviewModal(true)}>Preview</Button>
                                        <Button type="submit" variant="secondary mx-1 my-2" style={{ width: '90px' }} disabled={!this.state.formValid || this.state.templateFormLoading || this.state.templateStatus.type === 'success' }>Save {' '} {this.state.templateFormLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : null}</Button>
                                        </div>
                                    </div>    
                                </div>
                            </Form>
                        </div>
                }

                {/* modal to show template preview */}
                <Modal id="templatePreview" show={this.state.modal} size="lg" className="templateModal" animation={false} keyboard={false} onHide={() => { this.setState({ ...this.state }) }}>
                    <Modal.Header  style={{backgroundColor:'#00263E', color:'white' }}>
                        <div className="d-flex flex-row justify-content-between w-100">
                            <div className="d-flex flex-column">
                                <span className="">Location</span>
                                <span>{this.templateData['locationtitle']}</span>
                                <span className="">{this.state.formControls.type.value === 'T & C' ? 'Terms & Conditions' : this.state.formControls.type.value }</span>
                            </div>
                            <div className="d-flex flex-column justify-content-center align-items-center">
                                <span className="">Last Modified</span>
                                <span>{this.customDate()}</span>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                    <div className="ql-editor" style={{ padding: "1rem", textAlign:"justify" }} dangerouslySetInnerHTML={{ __html: this.state.formControls.template.value }} />
                    </Modal.Body>
                    <Modal.Footer className="justify-content-center">
                        <Button variant="secondary" onClick={() => this.togglePreviewModal(false)}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}